import React, {
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type RefAttributes,
  forwardRef,
  useMemo,
} from "react";
import { Field } from "react-final-form";
import type { InputProps } from "reactstrap";
import type {
  IFieldRendererProps,
  IFormInputLabelRequired,
} from "./IFormInput";
import InputRendererNew from "src/shared/forms/formrenderers/InputRendererNew";
import { combineValidations } from "src/shared/forms/validations/combineValidations";
import { length } from "src/shared/forms/validations/Length";
import { NoTrailingSpaceWithMessage } from "src/shared/forms/validations/NoTrailingSpace";
import Regex from "src/shared/forms/validations/Regex";
import { required } from "src/shared/forms/validations/Required";
import TextValidation from "src/shared/forms/validations/TextValidation";
import type { ValidationHandler } from "src/shared/forms/validations/ValidationHandler";

export interface ITextInputProps<FormData = Record<string, unknown>>
  extends InputHTMLAttributes<HTMLInputElement>,
    IFormInputLabelRequired<FormData>,
    Pick<InputProps, "valid"> {
  type?: "textarea" | "text" | "password" | "number";
  isRequired?: boolean;
  regex?: RegExp;
  isNoTrailingSpace?: boolean;
  noTrailingSpaceValidationMessage?: ReactNode;
  validators?: ValidationHandler | ValidationHandler[];
  minLength?: number;
  maxLength?: number;
  isText?: boolean;
  textValidationMessage?: ReactNode;

  readOnly?: boolean;
  autoComplete?: string;
  cols?: number;
  rows?: number;
}

export const TextInput = forwardRef(
  (
    {
      inputID,
      type = "text",
      isRequired,
      minLength,
      maxLength,
      isNoTrailingSpace,
      noTrailingSpaceValidationMessage,
      regex,
      validationDisabled,
      isText,
      textValidationMessage,
      validators,
      ...rest
    }: ITextInputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const validations = useMemo(() => {
      if (validationDisabled) {
        return undefined;
      }
      return combineValidations(
        isRequired && required,
        !!(minLength || maxLength) && length(minLength, maxLength),
        isNoTrailingSpace &&
          NoTrailingSpaceWithMessage(noTrailingSpaceValidationMessage),
        regex && Regex(regex),
        isText && TextValidation(textValidationMessage),
        validators,
      );
    }, [
      validationDisabled,
      isRequired,
      minLength,
      maxLength,
      isNoTrailingSpace,
      noTrailingSpaceValidationMessage,
      regex,
      isText,
      textValidationMessage,
      validators,
    ]);

    return (
      <Field
        name={inputID}
        render={(props: IFieldRendererProps<string | number>) => (
          <InputRendererNew {...rest} type={type} {...props} ref={ref} />
        )}
        validate={validations}
      />
    );
  },
) as (<FormData = Record<string, unknown>>(
  props: ITextInputProps<FormData> & RefAttributes<HTMLInputElement>,
) => JSX.Element) & { displayName?: string };
TextInput.displayName = "TextInput";

/**
 * @deprecated
 */
export default TextInput;
